import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription } from "./ui/alert";
import { ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, AlertTriangle, Info, Edit } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API client setup
const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const MonthlyCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState(null);
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [hourInputs, setHourInputs] = useState({});
  const [editMode, setEditMode] = useState(false); // New edit mode state

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  // Get all weekdays in the month - moved up to be used in fetchMonthlyData
  const getWeekdaysInMonth = () => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const weekdays = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      // Only include Monday-Friday (1-5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        weekdays.push({
          day,
          date,
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
          shortMonth: monthNames[month - 1].substr(0, 3)
        });
      }
    }
    
    return weekdays;
  };

  useEffect(() => {
    fetchMonthlyData();
  }, [year, month]);

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/monthly-data/${year}/${month}`);
      setMonthlyData(response.data);
      
      // Group projects by client
      const clientGroups = {};
      response.data.projects.forEach(project => {
        if (!clientGroups[project.client_name]) {
          clientGroups[project.client_name] = [];
        }
        clientGroups[project.client_name].push(project);
      });
      setClientData(Object.entries(clientGroups));
      
      // Initialize hour inputs from existing data for ALL project-day combinations
      const inputs = {};
      const weekdays = getWeekdaysInMonth();
      
      response.data.projects.forEach(project => {
        weekdays.forEach(weekday => {
          const key = `${project.id}_${weekday.day}`;
          const existingHours = project.daily_hours[weekday.day];
          inputs[key] = existingHours ? existingHours.toString() : '';
        });
      });
      // Only set hourInputs if we're in edit mode, otherwise clear them
      if (editMode) {
        setHourInputs(inputs);
      } else {
        setHourInputs({});
      }
    } catch (error) {
      console.error("Error fetching monthly data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Group weekdays by weeks
  const getWeekGroups = () => {
    const weekdays = getWeekdaysInMonth();
    const weeks = [];
    let currentWeek = [];
    
    weekdays.forEach((day, index) => {
      currentWeek.push(day);
      
      // End week on Friday or at the last day
      if (day.date.getDay() === 5 || index === weekdays.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });
    
    return weeks;
  };

  const navigateMonth = (direction) => {
    if (unsavedChanges) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to navigate away?")) {
        return;
      }
    }
    
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setUnsavedChanges(false);
    setHourInputs({});
    setEditMode(false); // Reset edit mode when navigating
  };

  const toggleEditMode = () => {
    if (editMode && unsavedChanges) {
      if (!window.confirm("You have unsaved changes. Exiting edit mode will lose these changes. Continue?")) {
        return;
      }
    }
    
    if (editMode) {
      // Exiting edit mode - clear all input state to prevent stale data
      setHourInputs({});
      setUnsavedChanges(false);
    }
    
    setEditMode(!editMode);
    if (!editMode) {
      // Entering edit mode - refresh hour inputs to ensure sync
      refreshHourInputs();
    }
  };

  const refreshHourInputs = () => {
    if (!monthlyData) return;
    
    if (editMode) {
      // Only populate inputs when in edit mode
      const inputs = {};
      const weekdays = getWeekdaysInMonth();
      
      monthlyData.projects.forEach(project => {
        weekdays.forEach(weekday => {
          const key = `${project.id}_${weekday.day}`;
          const existingHours = project.daily_hours[weekday.day];
          inputs[key] = existingHours ? existingHours.toString() : '';
        });
      });
      setHourInputs(inputs);
    } else {
      // Clear inputs when not in edit mode to prevent stale data
      setHourInputs({});
    }
  };

  const resetAllData = async () => {
    if (!window.confirm("Are you sure you want to reset ALL timesheet data for this month? This will delete all entered hours and cannot be undone.")) {
      return;
    }

    if (!window.confirm("FINAL WARNING: This will permanently delete all hours for " + monthNames[month - 1] + " " + year + ". Are you absolutely sure?")) {
      return;
    }

    setSaving(true);
    try {
      // Send empty entries to delete all hours for this month
      const entries = [];
      Object.keys(hourInputs).forEach(key => {
        const [projectId, day] = key.split('_');
        entries.push({
          project_id: projectId,
          date: new Date(year, month - 1, parseInt(day)).toISOString().split('T')[0],
          hours: 0, // Set to 0 to delete
          description: `Reset - ${monthNames[month - 1]} ${day}, ${year}`
        });
      });

      await apiClient.post('/monthly-hours/bulk', { entries });
      await fetchMonthlyData(); // Refresh data
      setUnsavedChanges(false);
      setEditMode(false);
      alert("All timesheet data has been reset successfully.");
    } catch (error) {
      console.error("Error resetting data:", error);
      alert("Error resetting data. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleHourChange = (projectId, day, value) => {
    const key = `${projectId}_${day}`;
    // Allow empty string to clear values
    setHourInputs(prev => ({
      ...prev,
      [key]: value // This will be '' if user clears the field
    }));
    setUnsavedChanges(true);
  };

  const saveMonthlyHours = async () => {
    setSaving(true);
    try {
      const entries = [];
      Object.entries(hourInputs).forEach(([key, hours]) => {
        const [projectId, day] = key.split('_');
        const numHours = parseFloat(hours);
        
        // Include ALL entries - even 0 hours for deletion, NaN becomes 0
        const finalHours = isNaN(numHours) ? 0 : numHours;
        
        // Create date string without timezone conversion to prevent date shifts
        const dateString = `${year}-${String(month).padStart(2, '0')}-${String(parseInt(day)).padStart(2, '0')}`;
        
        entries.push({
          project_id: projectId,
          date: dateString,
          hours: finalHours,
          description: `Daily work - ${monthNames[month - 1]} ${day}, ${year}`
        });
      });

      await apiClient.post('/monthly-hours/bulk', { entries });
      await fetchMonthlyData();
      setUnsavedChanges(false);
      // Clear hourInputs after successful save to prevent stale state
      setHourInputs({});
    } catch (error) {
      console.error("Error saving monthly hours:", error);
      alert("Error saving hours. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const finalizeMonth = async () => {
    // Prevent finalization if there are unsaved changes
    if (unsavedChanges) {
      alert("⚠️ You have unsaved changes!\n\nPlease save your hours by clicking 'Save Hours' before finalizing the month.");
      return;
    }

    if (!window.confirm(`Are you sure you want to finalize hours for ${monthNames[month - 1]} ${year}? This cannot be undone by team members.`)) {
      return;
    }

    try {
      await apiClient.post(`/monthly-hours/${year}/${month}/finalize`);
      await fetchMonthlyData();
      alert("Monthly hours finalized successfully!");
    } catch (error) {
      console.error("Error finalizing month:", error);
      alert("Error finalizing month. Please try again.");
    }
  };

  const calculateProjectTotal = (project) => {
    let total = 0;
    Object.entries(project.daily_hours).forEach(([day, hours]) => {
      const key = `${project.id}_${day}`;
      // Use saved data from backend unless we have unsaved changes in edit mode
      let currentHours;
      if (editMode && hourInputs.hasOwnProperty(key)) {
        // In edit mode, prioritize user input (including empty string for clearing)
        currentHours = parseFloat(hourInputs[key] || 0);
      } else {
        // Use saved data from backend
        currentHours = parseFloat(hours || 0);
      }
      total += currentHours;
    });
    return total;
  };

  const calculateProjectAverage = (project) => {
    const total = calculateProjectTotal(project);
    const weekdays = getWeekdaysInMonth();
    return weekdays.length > 0 ? (total / weekdays.length) : 0;
  };

  const getHourValue = (projectId, day) => {
    const key = `${projectId}_${day}`;
    // Only use hourInputs if we're in edit mode
    if (editMode && hourInputs.hasOwnProperty(key)) {
      return hourInputs[key];
    }
    // Use saved data from backend when not in edit mode
    const project = monthlyData?.projects.find(p => p.id === projectId);
    return project?.daily_hours[day] || '';
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading monthly data...</div>;
  }

  const weeks = getWeekGroups();
  const allWeekdays = getWeekdaysInMonth();

  return (
    <div className="space-y-6">
      {/* Month Header with Navigation */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(-1)}
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {monthNames[month - 1]} {year}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(1)}
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              {monthlyData?.is_finalized ? (
                <Badge variant="secondary" className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Finalized
                </Badge>
              ) : (
                <>
                  <Button
                    onClick={toggleEditMode}
                    variant={editMode ? "default" : "outline"}
                    size="sm"
                    data-testid="toggle-edit-btn"
                    className={editMode ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {editMode ? "Exit Edit Mode" : "Edit Hours"}
                  </Button>
                  
                  {editMode && (
                    <>
                      <Button
                        onClick={saveMonthlyHours}
                        disabled={!unsavedChanges || saving}
                        variant="outline"
                        size="sm"
                        data-testid="save-hours-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        {saving ? "Saving..." : "Save Hours"}
                      </Button>
                      
                      <Button
                        onClick={resetAllData}
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 border-red-300"
                        data-testid="reset-data-btn"
                      >
                        Reset All Data
                      </Button>
                    </>
                  )}
                  
                  <Button
                    onClick={finalizeMonth}
                    variant="default"
                    size="sm"
                    disabled={unsavedChanges}
                    data-testid="finalize-month-btn"
                    className={unsavedChanges ? "opacity-50 cursor-not-allowed" : ""}
                    title={unsavedChanges ? "Please save your hours first before finalizing" : "Finalize and submit your monthly hours"}
                  >
                    Send Final Hours
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{monthlyData?.total_hours || 0}h</div>
              <div className="text-sm text-gray-600">Total Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {monthlyData?.projects.reduce((sum, p) => sum + (p.monthly_budget || 0), 0)}h
              </div>
              <div className="text-sm text-gray-600">Total Budget</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {Math.max(0, monthlyData?.projects.reduce((sum, p) => sum + (p.monthly_budget || 0), 0) - (monthlyData?.total_hours || 0))}h
              </div>
              <div className="text-sm text-gray-600">Remaining</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {monthlyData?.projects.filter(p => p.budget_exceeded).length || 0}
              </div>
              <div className="text-sm text-gray-600">Over Budget</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {unsavedChanges && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            ⚠️ You have unsaved changes. Please click "Save Hours" before finalizing your month or navigating away. The "Send Final Hours" button is disabled until you save.
          </AlertDescription>
        </Alert>
      )}

      {monthlyData?.is_finalized && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            This month has been finalized. Hours cannot be edited by team members.
          </AlertDescription>
        </Alert>
      )}

      {/* Compact Timesheet Table */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Monthly Timesheet - {monthNames[month - 1]} {year}</CardTitle>
          <CardDescription className="text-sm">
            Enter your daily hours for each project. Weekdays only (M-F).
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Edit Mode Info Banner */}
          {!editMode && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 text-blue-800">
                <Info className="w-4 h-4" />
                <span className="text-sm">
                  <strong>View Mode:</strong> Your timesheet is currently read-only. 
                  Click "Edit Hours" to make changes to your timesheet.
                </span>
              </div>
            </div>
          )}
          
          {editMode && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center gap-2 text-green-800">
                <Edit className="w-4 h-4" />
                <span className="text-sm">
                  <strong>Edit Mode:</strong> You can now modify hours in the timesheet. 
                  Remember to click "Save Hours" when finished editing.
                </span>
              </div>
            </div>
          )}
          
          {unsavedChanges && editMode && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">
                  <strong>Unsaved Changes:</strong> You have unsaved changes. 
                  Don't forget to save before exiting edit mode.
                </span>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              {/* Header Row */}
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-2 text-left font-semibold text-xs min-w-[180px]">
                    Client / Project
                  </th>
                  {weeks.map((week, weekIndex) => (
                    <th key={weekIndex} className="border border-gray-300 p-1 text-center font-semibold text-xs" colSpan={week.length}>
                      Week {weekIndex + 1}
                    </th>
                  ))}
                  <th className="border border-gray-300 p-1 text-center font-semibold text-xs">Tot</th>
                  <th className="border border-gray-300 p-1 text-center font-semibold text-xs">Bud</th>
                  <th className="border border-gray-300 p-1 text-center font-semibold text-xs">Rem</th>
                </tr>
                
                {/* Day Headers */}
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-xs"></th>
                  {allWeekdays.map((day) => (
                    <th key={day.day} className="border border-gray-300 p-1 text-center text-xs min-w-[50px]">
                      <div className="font-medium">{day.dayName.charAt(0)}</div>
                      <div className="text-gray-600 text-xs">{day.day}</div>
                    </th>
                  ))}
                  <th className="border border-gray-300 p-1 text-xs">Tot</th>
                  <th className="border border-gray-300 p-1 text-xs">Bud</th>
                  <th className="border border-gray-300 p-1 text-xs">Rem</th>
                </tr>
              </thead>

              {/* Project Rows */}
              <tbody>
                {clientData.map(([clientName, projects]) => (
                  <React.Fragment key={clientName}>
                    {/* Client Header Row */}
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 p-3 font-bold text-blue-800" colSpan={allWeekdays.length + 4}>
                        {clientName}
                      </td>
                    </tr>
                    
                    {/* Project Rows */}
                    {projects.map((project) => {
                      const projectTotal = calculateProjectTotal(project);
                      const remaining = (project.monthly_budget || 0) - projectTotal;
                      
                      return (
                        <tr key={project.id} className="hover:bg-gray-50">
                          <td className="border border-gray-300 p-2 text-xs min-w-[180px]">
                            <div className="font-medium text-xs">{project.name}</div>
                            <div className="text-xs text-gray-500">
                              Avg: {calculateProjectAverage(project).toFixed(1)}h/day
                            </div>
                          </td>
                          
                          {/* Hour Input Cells */}
                          {allWeekdays.map((day) => (
                            <td key={day.day} className="border border-gray-300 p-0.5">
                              <input
                                type="number"
                                step="0.25"
                                min="0"
                                max="24"
                                value={getHourValue(project.id, day.day)}
                                onChange={(e) => handleHourChange(project.id, day.day, e.target.value)}
                                disabled={!editMode || monthlyData?.is_finalized}
                                readOnly={!editMode}
                                className={`text-center h-7 w-12 text-xs border-none outline-none ${
                                  editMode && !monthlyData?.is_finalized 
                                    ? 'bg-white focus:bg-blue-50 hover:bg-gray-50 cursor-text' 
                                    : 'bg-gray-100 cursor-default'
                                }`}
                                data-testid={`hour-input-${project.id}-${day.day}`}
                                style={{ 
                                  appearance: 'textfield',
                                  MozAppearance: 'textfield'
                                }}
                                onWheel={(e) => e.target.blur()}
                                title={editMode ? "Click to edit hours" : "Click 'Edit Hours' to modify"}
                              />
                            </td>
                          ))}
                          
                          {/* Summary Columns */}
                          <td className="border border-gray-300 p-1 text-center font-bold text-xs">
                            <span className={projectTotal > (project.monthly_budget || Infinity) ? "text-red-600" : ""}>
                              {projectTotal.toFixed(1)}
                            </span>
                          </td>
                          <td className="border border-gray-300 p-1 text-center text-xs">
                            {project.monthly_budget || '∞'}
                          </td>
                          <td className="border border-gray-300 p-1 text-center text-xs">
                            <span className={remaining < 0 ? "text-red-600 font-bold" : ""}>
                              {project.monthly_budget ? Math.max(0, remaining).toFixed(1) : '∞'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthlyCalendar;